<script setup lang="ts">
import { inject, computed, onMounted, onUnmounted } from 'vue'
import { useIncidentStore } from '@/stores/incident'
import { useOpsStore } from '@/stores/ops'
import { useCluesStore } from '@/stores/clues'
import { useFormsStore } from '@/stores/forms'
import { useSyncStore } from '@/stores/sync'
import FormShell from '@/components/forms/FormShell.vue'
import FosInput from '@/components/ui/FosInput.vue'
import { FormType } from '@/types'

const incidentStore = useIncidentStore()
const opsStore = useOpsStore()
const cluesStore = useCluesStore()
const formsStore = useFormsStore()
const syncStore = useSyncStore()
const incidentId = inject<string>('incidentId', '')

const FORM_TYPE = FormType.SAR134

const formData = computed(() => formsStore.getFormData(FORM_TYPE))
const incident = computed(() => incidentStore.currentIncident)
const op = computed(() => opsStore.selectedOp)

function field(key: string): string {
  const v = formData.value[key]
  return v != null ? String(v) : ''
}

async function save(fieldPath: string, event: Event) {
  if (!incidentId) return
  const value = (event.target as HTMLInputElement | HTMLTextAreaElement).value
  syncStore.pendingWrites++
  try {
    await formsStore.saveFormField(incidentId, FORM_TYPE, fieldPath, value)
  } finally {
    syncStore.pendingWrites--
  }
}

function formatCoords(lat?: number | null, lng?: number | null): string {
  if (lat == null || lng == null) return '—'
  return `${lat.toFixed(5)}, ${lng.toFixed(5)}`
}

function formatTime(ts: unknown): string {
  if (!ts) return '—'
  const d = (ts as { toDate?: () => Date }).toDate?.()
  if (!d) return '—'
  return d.toLocaleString()
}

onMounted(() => {
  if (incidentId) cluesStore.subscribeToClues(incidentId)
})

onUnmounted(() => {
  cluesStore.teardown()
})
</script>

<template>
  <FormShell
    :form-type="FORM_TYPE"
    title="SAR 134/135 · Clue / Evidence Report"
    back-route="incident-forms"
    back-label="Forms"
    :back-params="{ id: incidentId }"
  >
    <div class="max-w-[900px] mx-auto px-7 py-[22px] pb-16 flex flex-col gap-[18px]">
      <!-- Section head -->
      <div class="flex items-end gap-3.5 flex-wrap">
        <div class="flex flex-col gap-0.5">
          <span class="font-mono text-[11px] tracking-[0.12em] uppercase text-primary">SAR 134/135 · Clue / Evidence Report</span>
          <h2 class="font-headline font-black text-[26px] uppercase tracking-tight text-on-surface m-0">Clue / Evidence Report</h2>
        </div>
        <p class="flex-1 min-w-[200px] text-[13px] text-outline m-0">Live clue and evidence log for this incident. Updates in real time as clues are logged.</p>
      </div>

      <!-- Incident / OP (read-only) -->
      <div class="bg-surface border border-border rounded flex flex-col">
        <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
          <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Incident / Operational Period</h3>
          <span class="font-mono text-[9px] tracking-[0.10em] uppercase text-outline px-1.5 py-0.5 border border-border rounded bg-surface-container-low ml-auto">Read-only</span>
        </div>
        <div class="p-3.5 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div>
            <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Incident Name</label>
            <p class="font-body text-[13px] text-on-surface">{{ incident?.incident_name ?? '—' }}</p>
          </div>
          <div>
            <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Incident Number</label>
            <p class="font-body text-[13px] text-on-surface">{{ incident?.incident_number ?? '—' }}</p>
          </div>
          <div>
            <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Operational Period</label>
            <p class="font-mono text-[13px] text-on-surface">{{ op ? `OP ${op.op_number}` : '—' }}</p>
          </div>
          <div>
            <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">OP Date</label>
            <p class="font-body text-[13px] text-on-surface">{{ op?.date ?? '—' }}</p>
          </div>
        </div>
      </div>

      <!-- Clue Records (read-only, live from cluesStore) -->
      <div class="bg-surface border border-border rounded flex flex-col">
        <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
          <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Clue Records</h3>
          <span class="font-mono text-[11px] text-outline ml-auto">{{ cluesStore.clues.length }} clues</span>
        </div>

        <div v-if="cluesStore.clues.length === 0" class="p-8 text-center">
          <p class="font-mono text-[12px] text-outline">No clues logged for this incident.</p>
        </div>

        <div v-else class="overflow-x-auto">
          <table class="w-full text-[12px]">
            <thead>
              <tr class="border-b border-border/60">
                <th class="text-left px-3.5 py-2 font-mono text-[10px] tracking-[0.08em] uppercase text-outline font-medium">#</th>
                <th class="text-left px-3.5 py-2 font-mono text-[10px] tracking-[0.08em] uppercase text-outline font-medium">Description</th>
                <th class="text-left px-3.5 py-2 font-mono text-[10px] tracking-[0.08em] uppercase text-outline font-medium">Coordinates</th>
                <th class="text-left px-3.5 py-2 font-mono text-[10px] tracking-[0.08em] uppercase text-outline font-medium">Status</th>
                <th class="text-left px-3.5 py-2 font-mono text-[10px] tracking-[0.08em] uppercase text-outline font-medium">Found By</th>
                <th class="text-left px-3.5 py-2 font-mono text-[10px] tracking-[0.08em] uppercase text-outline font-medium">Date/Time</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(clue, i) in cluesStore.clues"
                :key="clue.clue_id"
                class="border-b border-border/30 last:border-0"
              >
                <td class="px-3.5 py-2 font-mono text-[11px] text-outline">{{ i + 1 }}</td>
                <td class="px-3.5 py-2 font-body text-on-surface max-w-[200px]">{{ clue.description }}</td>
                <td class="px-3.5 py-2 font-mono text-[11px] text-outline whitespace-nowrap">{{ formatCoords(clue.lat, clue.lng) }}</td>
                <td class="px-3.5 py-2">
                  <span
                    :class="[
                      'inline-flex items-center px-1.5 py-0.5 border rounded font-mono text-[10px] tracking-[0.06em] uppercase',
                      clue.status === 'confirmed' ? 'bg-green-50 border-green-300 text-green-700' :
                      clue.status === 'unconfirmed' ? 'bg-amber-50 border-amber-300 text-amber-700' :
                      'bg-surface-container-low border-border text-outline',
                    ]"
                  >{{ clue.status }}</span>
                </td>
                <td class="px-3.5 py-2 font-mono text-[11px] text-outline">{{ clue.found_by ?? '—' }}</td>
                <td class="px-3.5 py-2 font-mono text-[11px] text-outline whitespace-nowrap">{{ formatTime(clue.found_at) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Prepared By -->
      <div class="bg-surface border border-border rounded flex flex-col">
        <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
          <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Prepared By</h3>
        </div>
        <div class="p-3.5">
          <FosInput
            :model-value="field('prepared_by')"
            placeholder="Name of person preparing this report…"
            @blur="save('prepared_by', $event)"
          />
        </div>
      </div>
    </div>
  </FormShell>
</template>
